import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { Spinner } from "react-bootstrap";
import {
  assessmentStatusUrl,
  phlebotomyAssessmentStatusUrl,
  assessementFinalSubmitUrl,
  phlebotomyAssessementFinalSubmit,
} from "../../utils/constants";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import { EmojiSmile, XSquare } from "react-bootstrap-icons";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { isStepDataEmpty } from "../../utils/utils";
import TickIcon from "../../assets/images/step_success.svg";

function FinalStep() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { test_kit_id, bid, test_type } = useParams();
  const [isShTest, setIsShTest] = useState();
  const [isHormoneTest, setIsHormoneTest] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const [error, setError] = useState("");
  const formId = location.pathname.split("/").at(-1);
  const { setCompletedSteps, setIsCompleted, setStep, allStepData } = auth;
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const [isStepDataInvalid, setIsStepDataInvalid] = useState(false);
  const isPhlebotomy = location.pathname.includes("boots");
  const currentPath = globalThis.location.pathname;
  const parts = currentPath.split("/");
  const thisStep = parts[parts.length - 1];

  const toastShown = useRef(false);
  const hasSubmitted = useRef(false);
  const hasMergedData = useRef(false);
  const apiCalled = useRef(false);
  const hasSetLocalStorage = useRef(false);
  const isReloading = useRef(false);
  const navigationCheckDone = useRef(false); 

  const { callApi, resultGet, resultPost, serverError } = useApis();

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("currentPath", globalThis.location.pathname);
      localStorage.setItem("previous_step", thisStep);
    };

    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [thisStep]);

  useEffect(() => {
    if (resultPost) {
      setShowSpinner(false);
    }
  }, [resultPost]);

  useEffect(() => {
    const isCompleted = localStorage.getItem('assessment_completed');
    const savedTestKitId = localStorage.getItem('test_kit_id_completed');
    const currentTestKitId = test_kit_id || location?.state?.test_kit_id;
    
    if (isCompleted === 'true' && savedTestKitId === currentTestKitId) {
      setIs_assessment_completed(true);
      apiCalled.current = true;
      hasSetLocalStorage.current = true;
      isReloading.current = true; 
      navigationCheckDone.current = true; 
    }
  }, []);

  useEffect(() => {
    if (!apiCalled.current && !is_assessment_completed) {
      apiCalled.current = true;
      isReloading.current = false;
      
    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    } else if (!isPhlebotomy && !test_type && !bid) {
      test_kit_id &&
        localStorage.setItem("test_kit_id", location?.state?.test_kit_id);
      callApi(
        null,
        `${assessmentStatusUrl}${test_kit_id || localStorage.getItem("test_kit_id")}`,
        "GET",
        true,
      );
      }
    }
  }, []);

  useEffect(() => {
    if (resultGet?.is_assessment_completed === true && !hasSetLocalStorage.current) {
      setIs_assessment_completed(true);
      localStorage.setItem('assessment_completed', 'true');
      localStorage.setItem('test_kit_id_completed', test_kit_id || location?.state?.test_kit_id);
      hasSetLocalStorage.current = true;
      navigationCheckDone.current = true; 
      
      if (!toastShown.current) {
        auth?.showToast(
          "Assessment Completed",
          "All steps completed successfully",
          "../assets/mhc.png",
          <EmojiSmile />,
        );
        toastShown.current = true;
      }
    }
  }, [resultGet]);

  useEffect(() => {
    if (resultGet?.test_kit_details?.test_kit_type?.includes("sex")) {
      setIsShTest(true);
      setIsHormoneTest(false);
    } else if (
      resultGet?.test_kit_details?.test_kit_type?.includes("menopause") ||
      resultGet?.test_kit_details?.test_kit_type?.includes("polycystic")
    ) {
      setIsHormoneTest(true);
      setIsShTest(false);
    }
  }, [resultGet]);

  const preprocessStepData = (data) => {
    const processedData = { ...data };

    // Physical activity
    if (processedData.activity?.physical_activity === "No") {
      Object.assign(processedData.activity, {
        light_activity_hours_per_week: null,
        moderate_activity_hours_per_week: null,
        vigorous_activity_hours_per_week: null,
      });
    }

    // Alcohol
    const alcoholStatus = processedData.alcohol?.alcohol;
    if (alcoholStatus === "No" || alcoholStatus === "I recently quit") {
      processedData.alcohol.alcohol_units_per_week = null;
    }

    // Smoking
    const smokingStatus = processedData.smoking?.smoking;
    const smokingFields = {
      No: ["cigarettes_per_day", "how_many_year", "how_many_day_average"],
      Yes: ["how_many_year", "how_many_day_average"],
      "Ex-smoker": ["cigarettes_per_day"],
    };

    smokingFields[smokingStatus]?.forEach((field) => {
      processedData.smoking[field] = null;
    });

    // Body measurements
    if (!processedData.body?.height || !processedData.body?.weight) {
      processedData.body.height = null;
      processedData.body.weight = null;
    }

    if (processedData.body && resultGet?.test_kit_details?.sex === "male") {
      processedData.body.ispregnant = null;
      processedData.body.trimester = null;
    }

    // Pregnancy
    if (processedData.body?.ispregnant === "No") {
      processedData.body.trimester = null;
    }

    // Conditional fields clearing helper
    const clearIfNotIncludes = (section, field, value, targetField) => {
      if (
        processedData[section] &&
        !processedData[section][field]?.includes(value)
      ) {
        processedData[section][targetField] = "";
      }
    };

    // Medical conditions
    clearIfNotIncludes(
      "medical_conditions",
      "condition",
      "Other",
      "other_description",
    );
    clearIfNotIncludes(
      "medical_conditions",
      "condition",
      "Cancer",
      "cancer_type",
    );
    clearIfNotIncludes(
      "medical_conditions",
      "condition",
      "Vitamin/mineral deficiency",
      "deficiency",
    );

    // Medications
    clearIfNotIncludes(
      "medications",
      "medications",
      "Other",
      "medical_other_description",
    );

    clearIfNotIncludes(
      "medications",
      "medications",
      "Other",
      "other_description"
    );

    clearIfNotIncludes(
      "medications",
      "medications",
      "Cholesterol-lowering",
      "specify_medication",
    );

    // Supplements
    clearIfNotIncludes(
      "supplements",
      "supplements",
      "Other",
      "supplement_other_description",
    );

     clearIfNotIncludes(
      "supplements",
      "supplements",
      "Other",
      "other_description"
    ); 

    clearIfNotIncludes(
      "supplements",
      "supplements",
      "Iron, Folate",
      "supplement_details",
    );

    return processedData;
  };

  const finalSubmit = () => {
    if (hasSubmitted.current) return;
    hasSubmitted.current = true;

    const processedData = preprocessStepData(allStepData);

    setShowSpinner(true);
    if (isPhlebotomy && test_type && bid) {
      callApi(
        { ...processedData },
        phlebotomyAssessementFinalSubmit(bid),
        "POST",
      );
    } else {
      callApi(
        processedData,
        `${assessementFinalSubmitUrl}/${test_kit_id || location?.state?.test_kit_id}`,
        "POST",
        true,
      );
    }
  };

  useEffect(() => {
    if (
      !isStepDataInvalid &&
      resultGet &&
      !is_assessment_completed &&
      !hasSubmitted.current
    ) {
      if (resultGet?.assessment_values !== allStepData?.assessment_values) {
        finalSubmit();
      }
    }
  }, [resultGet, isStepDataInvalid, is_assessment_completed]);

  useEffect(() => {
    if (resultPost && isPhlebotomy && test_type && bid && !toastShown.current) {
      auth?.showToast(
        "Assessment Completed",
        "All steps completed successfully",
        "../assets/mhc.png",
        <EmojiSmile />,
      );
      toastShown.current = true;
      setIsCompleted(Number.parseInt(formId));
      setCompletedSteps(Number.parseInt(formId) + 1);
      setStep(Number.parseInt(formId));
      navigate(`/bloods/boots/success-page/${test_type}/${bid}`);
    }
  }, [resultPost, isPhlebotomy, test_type, bid]);

  useEffect(() => {
    if (!resultGet?.assessment_values || hasMergedData.current) return;

    if (resultGet?.list_of_steps) {
      auth.setTotalSteps(
        resultGet?.list_of_steps?.includes("phlebotomist_booking_info") ? 6 : 7,
      );
    }

    const mergedData = {
      medical_conditions: {
        is_diagnosed:
          allStepData?.medical_conditions?.is_diagnosed ??
          resultGet?.assessment_values?.medical_conditions?.is_diagnosed,
        condition:
          allStepData?.medical_conditions?.condition ??
          resultGet?.assessment_values?.medical_conditions?.condition,
        other_description:
          allStepData?.medical_conditions?.other_description ??
          resultGet?.assessment_values?.medical_conditions?.other_description,
        cancer_type:
          allStepData?.medical_conditions?.cancer_type ??
          resultGet?.assessment_values?.medical_conditions?.cancer_type,
        deficiency:
          allStepData?.medical_conditions?.deficiency ??
          resultGet?.assessment_values?.medical_conditions?.deficiency,
      },
      medications: {
        medication:
          allStepData?.medications?.medication ??
          resultGet?.assessment_values?.medications?.medication,
        medications:
          allStepData?.medications?.medications ??
          resultGet?.assessment_values?.medications?.medications,
        other_description:
          allStepData?.medications?.other_description ??
          resultGet?.assessment_values?.medications?.other_description,
        specify_medication:
          allStepData?.medications?.specify_medication ??
          resultGet?.assessment_values?.medications?.specify_medication,
      },
      supplements: {
        taking_supplements:
          allStepData?.supplements?.taking_supplements ??
          resultGet?.assessment_values?.supplements?.taking_supplements,
        supplements:
          allStepData?.supplements?.supplements ??
          resultGet?.assessment_values?.supplements?.supplements,
        other_description:
          allStepData?.supplements?.other_description ??
          resultGet?.assessment_values?.supplements?.other_description,
        supplement_details:
          allStepData?.supplements?.supplement_details ??
          resultGet?.assessment_values?.supplements?.supplement_details,
      },
      body: {
        ...(allStepData?.body || resultGet?.assessment_values?.body),
      },
      smoking: {
        ...(allStepData?.smoking || resultGet?.assessment_values?.smoking),
      },
      alcohol: {
        ...(allStepData?.alcohol || resultGet?.assessment_values?.alcohol),
      },
      activity: {
        ...(allStepData?.activity || resultGet?.assessment_values?.activity),
      },
    };

    if (resultGet.test_kit_details?.test_kit_type?.includes("sex")) {
      mergedData.sexual_health =
        allStepData?.sexual_health ||
        resultGet?.assessment_values?.sexual_health;
    } else if (
      resultGet.test_kit_details?.test_kit_type?.includes("menopause") ||
      resultGet.test_kit_details?.test_kit_type?.includes("polycystic")
    ) {
      mergedData.menstrual =
        allStepData?.menstrual || resultGet?.assessment_values?.menstrual;
    }

    auth.setAllStepData(mergedData);
    hasMergedData.current = true;
  }, [resultGet]);

  useEffect(() => {
    if (resultPost && !test_type && !bid && !toastShown.current) {
      auth?.showToast(
        "Assessment Completed",
        "All steps completed successfully",
        "../assets/mhc.png",
        <EmojiSmile />,
      );
      toastShown.current = true;
    }
  }, [resultPost, test_type, bid]);

  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  useEffect(() => {
    if (is_assessment_completed || isReloading.current || navigationCheckDone.current) {
      return;
    }
    if (resultGet?.assessment_values) {
      navigationCheckDone.current = true;
      return;
    }
    if (!resultGet && !error) {
      return;
    }
    if (isStepDataEmpty(allStepData) && !resultGet) {
      setIsStepDataInvalid(true);
      auth?.showToast(
        "Data Lost",
        "Previous step data is missing. Please complete the assessment steps again.",
        "../assets/mhc.png",
        <XSquare />,
      );
      navigate(`/assessment/${test_kit_id || location?.state?.test_kit_id}/1`);
    }
  }, [is_assessment_completed, resultGet, allStepData, test_kit_id, location?.state?.test_kit_id, navigate, error]);

  useEffect(() => {
    setError(serverError);
  }, [serverError]);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes(`/assessment/${test_kit_id}/5`)) {
        localStorage.removeItem('assessment_completed');
        localStorage.removeItem('test_kit_id_completed');
      }
    };
  }, []);

  if (((!resultGet && !error) || showSpinner) && !is_assessment_completed && !isReloading.current) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  if (error && !isReloading.current) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  return (
    <div className="final-step-container">
      <div className="final-step-card">
        <div className="">
          <div className="justify-content-center">
            <div className="assessment-form mt-1">
              <h1 className="mb-4">Test registration and health assessment completed</h1>
              <p>
                Your test registration has been successfully completed. You can view your health information in the Account section.
              </p>
              <button
                onClick={() => navigate("/")}
                className="btn btn-primary mb-4 w-100"
              >
                My Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinalStep;